import React from 'react';

/**
 * Calculates days from today to a given date string (YYYY-MM-DD).
 * Uses noon UTC to avoid off-by-one from timezone differences.
 */
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr + 'T12:00:00');
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

/**
 * RecordStatusBanner
 *
 * Row 1 (when reviewDate is set): review countdown — always shown, urgency increases as date nears.
 * Row 2: single contextual nudge based on record health (one at a time, highest priority first):
 *   1. No highlights → prompt to highlight something
 *   2. Has highlights but none verified → prompt to request endorsement
 *   3. Has verified achievements → celebrate + prompt to share
 */
export default function RecordStatusBanner({ achievements = [], onRequestEndorsement, reviewDate }) {
  const total = achievements.length;
  const highlighted = achievements.filter(a => !!a.is_highlight);
  const verified = achievements.filter(a => !!a.endorsed);

  const highlightCount = highlighted.length;
  const verifiedCount = verified.length;
  const days = daysUntil(reviewDate);

  // Nothing to show on empty state (handled by parent)
  if (total === 0 && days === null) return null;

  // --- Review date countdown row ---
  const reviewRow = (() => {
    if (days === null) return null;

    let bgColor, textColor, borderColor, icon, message;

    if (days < 0) {
      bgColor = 'bg-gray-50'; textColor = 'text-gray-600'; borderColor = 'border-gray-200';
      message = `Your review date has passed. Update it in Profile settings.`;
      icon = (
        <svg className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    } else if (days <= 14) {
      bgColor = 'bg-red-50'; textColor = 'text-red-800'; borderColor = 'border-red-200';
      message = days === 0
        ? `Your review is today. Is your record ready?`
        : `Your review is in ${days} day${days === 1 ? '' : 's'}. Is your record ready?`;
      icon = (
        <svg className="w-4 h-4 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    } else if (days <= 30) {
      bgColor = 'bg-amber-50'; textColor = 'text-amber-800'; borderColor = 'border-amber-200';
      message = `Your review is in ${days} days — ${verifiedCount} achievement${verifiedCount !== 1 ? 's' : ''} verified so far.`;
      icon = (
        <svg className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    } else {
      bgColor = 'bg-gray-50'; textColor = 'text-gray-600'; borderColor = 'border-gray-200';
      message = `Your review is in ${days} days — ${total} logged, ${verifiedCount} verified.`;
      icon = (
        <svg className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    }

    return (
      <div className={`mx-4 md:mx-8 mb-2 flex items-start gap-2.5 ${bgColor} border ${borderColor} rounded-xl px-4 py-2.5`}>
        {icon}
        <p className={`text-sm ${textColor}`}>{message}</p>
      </div>
    );
  })();

  // Nothing to show if no review date and no achievements
  if (total === 0 && !reviewRow) return null;

  // --- Health nudge (one of three states) ---
  let healthNudge = null;

  if (total > 0) {
    if (highlightCount === 0) {
      healthNudge = (
        <div className="mx-4 md:mx-8 mb-3 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <svg className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
          </svg>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">Nothing on your public profile yet</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Highlight your best achievement to add it to your public profile — the one you'd want a recruiter to see first.
            </p>
          </div>
        </div>
      );
    } else if (verifiedCount === 0) {
      const firstHighlight = highlighted[0];
      healthNudge = (
        <div className="mx-4 md:mx-8 mb-3 flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <svg className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-blue-900">
              {highlightCount === 1
                ? "Your highlight isn't verified yet"
                : `${highlightCount} highlights — none verified yet`}
            </p>
            <p className="text-xs text-blue-700 mt-0.5">
              A verified achievement is far more credible to recruiters than a self-reported one.{' '}
              {onRequestEndorsement && firstHighlight && (
                <button
                  onClick={() => onRequestEndorsement(firstHighlight)}
                  className="font-semibold underline hover:no-underline"
                >
                  Ask your manager to verify one →
                </button>
              )}
            </p>
          </div>
        </div>
      );
    } else {
      healthNudge = (
        <div className="mx-4 md:mx-8 mb-3 flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-green-800 flex-1 min-w-0">
            <span className="font-semibold">
              {verifiedCount} verified {verifiedCount === 1 ? 'achievement' : 'achievements'}
            </span>
            {' '}— your record has proof behind it.{' '}
            <a href="/profile" className="font-semibold underline hover:no-underline">
              Share your profile →
            </a>
          </p>
        </div>
      );
    }
  }

  if (!reviewRow && !healthNudge) return null;

  return (
    <>
      {reviewRow}
      {healthNudge}
    </>
  );
}
